import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  listJobApps, 
  upsertJobApp, 
  deleteJobApp, 
  uploadResume, 
  getSignedResumeUrl, 
  deleteResume,
  jobAppStatuses 
} from '../services/jobTracker';
import QRCode from 'qrcode';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import AdSense from '../components/AdSense';
import { useNavigate } from 'react-router-dom';
import { formatPhoneNumber, formatPhoneInput } from '../utils/phoneFormatter';

const emptyJobApp = {
  job_title: '',
  company: '',
  date_applied: new Date().toISOString().slice(0, 10),
  status: 'Applied',
  notes: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  resumeFile: null
};

export default function JobTracker() {
  const { user, isPremium, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(emptyJobApp);
  const [uploading, setUploading] = useState(null);
  const [qrFor, setQrFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect if not premium
  useEffect(() => {
    if (!authLoading && !isPremium) {
      navigate('/subscription-management?upgrade=true');
    }
  }, [isPremium, authLoading, navigate]);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await listJobApps();
      setItems(data || []);
    } catch (err) {
      console.error('Error loading job applications:', err);
      setError('Failed to load job applications. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isPremium) {
      refresh();
    }
  }, [isPremium]);

  const canSave = useMemo(() =>
    draft.job_title.trim() && draft.company.trim(), [draft]);

  async function save() {
    if (!canSave) return;
    
    setError(null);
    try {
      // Remove resumeFile from the data before saving to database
      const { resumeFile, ...applicationData } = draft;
      
      // First save the application
      const saved = await upsertJobApp(applicationData);
      
      // If there's a resume file, upload it
      if (resumeFile && saved.id) {
        try {
          const updated = await uploadResume(resumeFile, saved.id);
          setItems(prev => {
            const exists = prev.find(i => i.id === updated.id);
            return exists ? prev.map(i => i.id === updated.id ? updated : i) : [updated, ...prev];
          });
        } catch (uploadErr) {
          console.error('Error uploading resume:', uploadErr);
          setError('Application saved but resume upload failed. You can upload it later.');
        }
      } else {
        setItems(prev => {
          const exists = prev.find(i => i.id === saved.id);
          return exists ? prev.map(i => i.id === saved.id ? saved : i) : [saved, ...prev];
        });
      }
      
      setDraft(emptyJobApp);
    } catch (err) {
      console.error('Error saving job application:', err);
      setError('Failed to save job application. Please try again.');
    }
  }

  async function onUploadResume(app, file) {
    if (!file || !app.id) return;
    
    setUploading(app.id);
    setError(null);
    try {
      const updated = await uploadResume(file, app.id);
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    } catch (err) {
      console.error('Error uploading resume:', err);
      setError('Failed to upload resume. Please try again.');
    } finally {
      setUploading(null);
    }
  }

  async function onDeleteResume(app) {
    if (!app.id || !app.resume_path) return;
    
    setError(null);
    try {
      await deleteResume(app.id);
      setItems(prev => prev.map(i => 
        i.id === app.id ? { ...i, resume_path: null } : i
      ));
    } catch (err) {
      console.error('Error deleting resume:', err);
      setError('Failed to delete resume. Please try again.');
    }
  }

  async function openShare(app) {
    if (!app.resume_path) {
      setError('No resume uploaded yet.');
      return;
    }
    
    setError(null);
    try {
      const url = await getSignedResumeUrl(app.resume_path);
      const dataUrl = await QRCode.toDataURL(url);
      setQrFor({ 
        url: dataUrl, 
        label: `${app.company} – ${app.job_title}`,
        shareUrl: url
      });
    } catch (err) {
      console.error('Error generating share link:', err);
      setError('Failed to generate share link. Please try again.');
    }
  }

  async function downloadResume(app) {
    if (!app.resume_path) {
      setError('No resume uploaded yet.');
      return;
    }
    
    setError(null);
    try {
      const url = await getSignedResumeUrl(app.resume_path);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = url;
      link.download = `${app.company}_${app.job_title}_resume.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading resume:', err);
      setError('Failed to download resume. Please try again.');
    }
  }

  async function viewResume(app) {
    if (!app.resume_path) {
      setError('No resume uploaded yet.');
      return;
    }
    
    setError(null);
    try {
      const url = await getSignedResumeUrl(app.resume_path);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening resume:', err);
      setError('Failed to open resume. Please try again.');
    }
  }

  async function handleDelete(app) {
    if (!confirm(`Delete application for ${app.job_title} at ${app.company}?`)) return;
    
    setError(null);
    try {
      await deleteJobApp(app.id);
      setItems(prev => prev.filter(i => i.id !== app.id));
    } catch (err) {
      console.error('Error deleting job application:', err);
      setError('Failed to delete job application. Please try again.');
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isPremium) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header user={user} />
      
      <main className="flex-1 pt-32 pb-16" style={{ paddingTop: 'calc(8rem + 30px)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="Briefcase" size={32} className="text-primary" />
                <h1 className="text-3xl font-bold text-foreground">
                  Job Application Tracker
                </h1>
              </div>
              <p className="text-muted-foreground">
                Track your job applications, upload resumes, and share with employers
              </p>
            </div>

            {/* Page Audio */}
            <div className="flex justify-center mb-8">
              <audio
                src="/assets/audio/Job_Tracker.mp3"
                controls
                preload="metadata"
                className="h-8 max-w-xs"
              />
            </div>

            {/* AdSense - Banner for Free Users */}
            {!isPremium && (
              <div className="mb-8">
                <AdSense 
                  adSlot="1234567890"
                  adFormat="auto"
                  className="w-full"
                />
              </div>
            )}

            <div className="space-y-8">

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <span className="text-error">{error}</span>
              <button 
                onClick={() => setError(null)}
                className="ml-auto text-error hover:text-error/80"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {/* Add/Edit Form */}
          <section className="bg-card border border-border rounded-xl p-6 space-y-6">
            <h2 className="text-xl font-semibold text-foreground">Add New Application</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                label="Job Title *"
                value={draft.job_title}
                onChange={v => setDraft(s => ({ ...s, job_title: v }))}
                placeholder="e.g., Senior Software Engineer"
              />
              <Input
                label="Company *"
                value={draft.company}
                onChange={v => setDraft(s => ({ ...s, company: v }))}
                placeholder="e.g., Tech Corp"
              />
              <Input
                label="Date Applied"
                type="date"
                value={draft.date_applied}
                onChange={v => setDraft(s => ({ ...s, date_applied: v }))}
              />
              <Input
                label="Contact Name"
                value={draft.contact_name || ''}
                onChange={v => setDraft(s => ({ ...s, contact_name: v }))}
                placeholder="e.g., John Smith"
              />
              <Input
                label="Contact Email"
                type="email"
                value={draft.contact_email || ''}
                onChange={v => setDraft(s => ({ ...s, contact_email: v }))}
                placeholder="e.g., john@company.com"
              />
              <Input
                label="Contact Phone"
                value={draft.contact_phone || ''}
                onChange={v => setDraft(s => ({ ...s, contact_phone: formatPhoneInput(v) }))}
                placeholder="e.g., (555) 123-4567"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <select
                  className="w-full border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={draft.status}
                  onChange={e => setDraft(s => ({ ...s, status: e.target.value }))}
                >
                  {jobAppStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
              <textarea
                className="w-full border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
                value={draft.notes || ''}
                onChange={e => setDraft(s => ({ ...s, notes: e.target.value }))}
                placeholder="Add any notes about this application..."
              />
            </div>

            {/* Resume Upload Section */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Resume Upload
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  id="resume-upload"
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setDraft(s => ({ ...s, resumeFile: file }));
                    }
                  }}
                />
                <label htmlFor="resume-upload" className="cursor-pointer">
                  <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-1">
                    {draft.resumeFile ? draft.resumeFile.name : 'Click to upload resume'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PDF, DOC, or DOCX files accepted
                  </p>
                </label>
                {draft.resumeFile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => setDraft(s => ({ ...s, resumeFile: null }))}
                  >
                    <Icon name="X" size={14} className="mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={save}
                disabled={!canSave}
                className="bg-primary hover:bg-primary/90 disabled:opacity-50"
              >
                <Icon name="Save" size={16} className="mr-2" />
                Save Application
              </Button>
              <Button
                variant="outline"
                onClick={() => setDraft(emptyJobApp)}
              >
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Reset
              </Button>
            </div>
          </section>

          {/* Applications List */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Your Applications</h2>
              <div className="text-sm text-muted-foreground">
                {loading ? 'Loading...' : `${items.length} application${items.length !== 1 ? 's' : ''}`}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading applications...</p>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-xl">
                <Icon name="Briefcase" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No applications yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start tracking your job applications by adding your first one above.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map(app => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    onUploadResume={onUploadResume}
                    onDeleteResume={onDeleteResume}
                    onShare={openShare}
                    onDownload={downloadResume}
                    onView={viewResume}
                    onDelete={handleDelete}
                    uploading={uploading === app.id}
                  />
                ))}
              </div>
            )}
          </section>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />

      {/* QR Code Modal */}
      {qrFor && (
        <div 
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={() => setQrFor(null)}
        >
          <div 
            className="bg-card border border-border rounded-xl p-6 w-full max-w-sm"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Share Resume
            </h3>
            <p className="text-sm text-muted-foreground mb-4">{qrFor.label}</p>
            
            <div className="text-center mb-4">
              <img 
                src={qrFor.url} 
                alt="QR code" 
                className="w-48 h-48 mx-auto rounded-lg border border-border"
              />
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(qrFor.shareUrl);
                  setQrFor(null);
                }}
                className="w-full bg-primary hover:bg-primary/90"
              >
                <Icon name="Copy" size={16} className="mr-2" />
                Copy Link
              </Button>
              <Button
                variant="outline"
                onClick={() => setQrFor(null)}
                className="w-full"
              >
                Close
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-4 text-center">
              Scan QR code or use the link to access the resume. Links expire in 7 days for security.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ApplicationCard({ app, onUploadResume, onDeleteResume, onShare, onDownload, onView, onDelete, uploading }) {
  const [fileInputKey, setFileInputKey] = useState(0);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUploadResume(app, file);
      setFileInputKey(prev => prev + 1);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Applied': 'bg-blue-100 text-blue-800',
      'Interviewing': 'bg-yellow-100 text-yellow-800',
      'Offer': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'On Hold': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <article className="bg-card border border-border rounded-xl p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-semibold text-foreground">
              {app.job_title}
            </h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
              {app.status}
            </span>
          </div>
          
          <p className="text-muted-foreground mb-2">{app.company}</p>
          
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Applied: {new Date(app.date_applied).toLocaleDateString()}</p>
            
            {(app.contact_name || app.contact_email || app.contact_phone) && (
              <div className="flex flex-wrap gap-2">
                {app.contact_name && (
                  <span className="flex items-center space-x-1">
                    <Icon name="User" size={12} />
                    <span>{app.contact_name}</span>
                  </span>
                )}
                {app.contact_email && (
                  <span className="flex items-center space-x-1">
                    <Icon name="Mail" size={12} />
                    <span>{app.contact_email}</span>
                  </span>
                )}
                {app.contact_phone && (
                  <span className="flex items-center space-x-1">
                    <Icon name="Phone" size={12} />
                    <span>{formatPhoneNumber(app.contact_phone)}</span>
                  </span>
                )}
              </div>
            )}
            
            {app.notes && (
              <p className="mt-2 text-sm text-muted-foreground italic">
                "{app.notes}"
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <label className="px-4 py-2 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors flex items-center justify-center space-x-2">
            <input
              key={fileInputKey}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
            />
            <Icon name="Upload" size={16} />
            <span>
              {uploading ? 'Uploading...' : (app.resume_path ? 'Replace Resume' : 'Upload Resume')}
            </span>
          </label>

          {app.resume_path && (
            <>
              <Button
                onClick={() => onView(app)}
                variant="outline"
                className="text-primary hover:text-primary"
              >
                <Icon name="Eye" size={16} className="mr-2" />
                View
              </Button>
              <Button
                onClick={() => onDownload(app)}
                variant="outline"
                className="text-primary hover:text-primary"
              >
                <Icon name="Download" size={16} className="mr-2" />
                Download
              </Button>
              <Button
                onClick={() => onShare(app)}
                className="bg-primary hover:bg-primary/90"
              >
                <Icon name="Share" size={16} className="mr-2" />
                Share
              </Button>
              <Button
                variant="outline"
                onClick={() => onDeleteResume(app)}
                className="text-error hover:text-error"
              >
                <Icon name="Trash2" size={16} className="mr-2" />
                Delete Resume
              </Button>
            </>
          )}

          <Button
            variant="outline"
            onClick={() => onDelete(app)}
            className="text-error hover:text-error border-error/20 hover:border-error/40"
          >
            <Icon name="Trash2" size={16} className="mr-2" />
            Delete
          </Button>
        </div>
      </div>
    </article>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {label}
      </label>
      <input
        className="w-full border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
        value={value}
        type={type}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
